import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { useFileUploadToField } from "../../../api/useFileUploadToField";
import { useContext, useEffect } from "react";
import { RecordContext } from "../../record/Record";
import { useFilesData } from "../../../api/useFilesData";
import { Unstable_Grid2 as Grid } from "@mui/material";
import { Delete, OpenInNew, TextSnippetRounded } from "@mui/icons-material";
import { useCreateFileUrl } from "../../../api/useCreateFileUrl";
import { Link, useNavigate } from "react-router-dom";
import { FieldExtraOption, FieldView, FormatedField } from "../../../types/fields.types";
import { useDeleteFile } from "../../../api/useDeleteFile";
import { useStore } from "../../../store/store";

type FileFieldProps = {
    entity?: string;
    id?: string | number;
    value: Array<{
        id: string | number;
        directus_files_id: string;
        [key: string]: any;
    }> | {
        id: string;
        [key: string]: any;
    };
    field: FormatedField;
    fieldView: FieldView;
    extraOptions: FieldExtraOption;
};

export const FileField = ({ entity, id, value, field, fieldView, extraOptions }: FileFieldProps) => {
    const context = useContext(RecordContext);
    const instanceToUse = extraOptions?.instanceToUse;
    const additionalData = extraOptions?.additionalData;
    
    const entityToUse = entity || context?.entity;
    const idToUse = id || context?.id;

    const deleteFile = useDeleteFile(undefined, instanceToUse);
    const { refetchFiles, notifications } = useStore();

    const navigate = useNavigate();

    const idToUseinGrid = Array.isArray(value) ? value[0]?.[`${field?.settings?.entity}_id`] : value?.id;

    const { mutate, isLoading, isError } = useFileUploadToField(field, instanceToUse, additionalData);
    const fieldId = fieldView === "group" ? idToUse : idToUseinGrid;

    const { data, status, refetch, error } = useFilesData({
        entity: entityToUse,
        id: fieldId,
        field: field.field,
        directus_files: value,
        instanceToUse: instanceToUse,
    });

    const createFileUrl = useCreateFileUrl();

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = event.target.files;
    
        let isValidFile = true;
        
        if (fileList && fileList.length > 0) {
            const file = fileList[0];
            const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
            
            if(extraOptions?.isValidFileExtension && !extraOptions?.isValidFileExtension(fileExtension)){
                isValidFile = false;
            }
        }

        if(!isValidFile){
            notifications.setNotification({
                isOpen: true,
                message: 'Ficheiro inválido',
                severity: 'error',
            });
            return;
        }
        if (!entityToUse || !idToUse) return;

        if (extraOptions?.onUploadStart) extraOptions.onUploadStart();

        mutate({
            file: fileList![0],
            entity: entityToUse,
            itemId: idToUse,
            folder: extraOptions.fileFolder,
            instanceToUse: instanceToUse,
        }, {onSettled: () => {
            if (extraOptions?.onUploadEnd) extraOptions.onUploadEnd();
        }});
    };

    useEffect(() => {
        if (refetchFiles.timestamp && refetchFiles.timestamp > 0) {
            const timeoutId = setTimeout(() => {
                refetch();
            }, 1000);

            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchFiles.timestamp]);

    if (status === "loading") return <CircularProgress />;
    if (status === "error") {
        if (error && (error as any).response?.statusText === "Forbidden") return null;
        return <Typography color="error">Erro ao carregar ficheiros</Typography>
    }

    if (fieldView === "grid") {

        return (
            <Box>
                {data?.map((file) => {
  
                    return (
                        <IconButton
                            key={file.id}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            component={Link}
                            to={createFileUrl(file.id, instanceToUse)}
                            target="_blank"
                            size="medium"
                            color="primary"
                        >
                            <TextSnippetRounded />
                        </IconButton>
                    );
                })}
            </Box>
        );
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", ...extraOptions?.style?.fileList }}>
            {extraOptions?.hidden?.fileList !== true &&
                data?.map((file) => {
                    return (
                        <Grid container key={file.id} alignItems="center">
                            <Grid display="flex" alignItems="center">
                                <Button component={Link} to={createFileUrl(file.id, instanceToUse)} target="_blank" variant="text" size="small">
                                    <Typography marginRight={1} color="secondary">
                                        {" "}
                                        {fileTitle(file.title)}
                                    </Typography>
                                    <OpenInNew color="secondary" />
                                </Button>

                                {!extraOptions?.hidden?.detail && (
                                    <IconButton
                                        onClick={() => {
                                            if (!entityToUse) return;
                                            if (extraOptions?.fileDeletingInterceptor) {
                                                extraOptions.fileDeletingInterceptor({
                                                    deleteFile: deleteFile,
                                                    fileId: file.id,
                                                    item: context?.item,
                                                    navigate: navigate,
                                                });
                                                return;
                                            }
                                            const confirm = window.confirm("Tem a certeza que quer apagar este ficheiro?");
                                            if (!confirm) return;
                                            deleteFile.mutate({ id: file.id, entity: entityToUse, instanceToUse: instanceToUse });
                                        }}
                                        size="small"
                                        color="error"
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    );
                })}

            {isLoading ? (
                <CircularProgress />
            ) : isError ? (
                <Typography>Não foi possível fazer upload</Typography>
            ) : extraOptions?.hidden?.detail !== true ? (
                <>
                    {data.length > 0 && field.settings.interface !== "file-image" && extraOptions?.hidden?.update !== true && (
                        <Button
                            disabled={extraOptions?.disabled}
                            component="label"
                            size="small"
                            variant={extraOptions?.style?.uploadButton?.variant || "outlined"}
                            sx={{ color: extraOptions?.style?.uploadButton?.color || "grey" }}
                        >
                            Carregar Outro Ficheiro
                            <input type="file" hidden onChange={handleFileSelect} />
                        </Button>
                    )}
                    {data.length === 0 && extraOptions?.hidden?.create !== true && (
                        <Button disabled={extraOptions?.disabled} component="label">
                            Carregar Ficheiro
                            <input type="file" hidden onChange={handleFileSelect} />
                        </Button>
                    )}
                </>
            ) : null}
        </Box>
    );
};

export const fileTitle = (fileTitle: string) => {
    const maxFileTitleLength = 20;
    if (fileTitle.length < maxFileTitleLength) {
        return fileTitle;
    }

    return fileTitle.substring(0, 20) + "...";
};
